import { Team } from "../types"
import { getWikimediaCollegeLogoUrlsSync, getWikimediaCollegeLogoAsync } from "../../lib/wikimedia-college-logos"

// Cache for enhanced logo URLs found via API
const enhancedLogoCache = new Map<string, string>();

/**
 * Background service to enhance college team logos using Wikimedia API
 * This runs asynchronously and updates the cache
 */
export async function enhanceCollegeTeamLogo(team: Team): Promise<void> {
  if (team.sport !== 'College' || !team.abbreviation || !team.name) {
    return;
  }
  
  const cacheKey = `${team.abbreviation}-${team.name}`;
  
  // Skip if already cached
  if (enhancedLogoCache.has(cacheKey)) {
    return;
  }
  
  try {
    const logoUrl = await getWikimediaCollegeLogoAsync(team.abbreviation, team.name);
    if (logoUrl) {
      enhancedLogoCache.set(cacheKey, logoUrl);
      console.log(`Enhanced logo found for ${team.name}:`, logoUrl);
    } else {
      // Cache the miss to avoid repeated lookups
      enhancedLogoCache.set(cacheKey, '');
    }
  } catch (error) {
    console.warn(`Failed to enhance logo for ${team.name}:`, error);
    enhancedLogoCache.set(cacheKey, '');
  }
}

/**
 * Get enhanced logo URL from cache
 */
export function getEnhancedCollegeLogo(team: Team): string | null {
  if (team.sport !== 'College' || !team.abbreviation || !team.name) {
    return null;
  }
  
  const cacheKey = `${team.abbreviation}-${team.name}`;
  const cached = enhancedLogoCache.get(cacheKey);
  return cached && cached !== '' ? cached : null;
}

/**
 * Extracts ESPN team ID from our team ID format
 * Format: "league-espnId" (e.g., "nba-1610612737", "mlb-108")
 */
export function getEspnTeamId(teamId: string): string | null {
  const parts = teamId.split('-')
  if (parts.length >= 2) {
    return parts.slice(1).join('-') // Join back in case there are multiple dashes
  }
  return null
}

/**
 * Gets ESPN logo URL for a team
 * Note: These URLs often return 404s, so always use with fallback handling
 */
export function getEspnLogoUrl(team: Team): string | null {
  if (!team.abbreviation) return null
  
  const league = team.league?.toLowerCase() || ''
  
  // Use the same ESPN CDN format that works in the modal
  if (league.includes('nfl')) {
    return `https://a.espncdn.com/i/teamlogos/nfl/500/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('nba')) {
    return `https://a.espncdn.com/i/teamlogos/nba/500/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('mlb') || league.includes('american league') || league.includes('national league')) {
    return `https://a.espncdn.com/i/teamlogos/mlb/500/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('nhl')) {
    return `https://a.espncdn.com/i/teamlogos/nhl/500/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('mls')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/mls/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('wnba')) {
    return `https://a.espncdn.com/i/teamlogos/wnba/500/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('nwsl')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/nwsl/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('premier league')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/eng.1/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('la liga')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/esp.1/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('bundesliga')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/ger.1/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('serie a')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/ita.1/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('ligue 1')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/fra.1/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('liga mx')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/mex.1/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('brazilian serie a')) {
    return `https://a.espncdn.com/i/teamlogos/soccer/500/bra.1/${team.abbreviation.toLowerCase()}.png`
  } else if (['uefa', 'conmebol', 'concacaf', 'afc', 'caf', 'ofc'].some(prefix => league.includes(prefix))) {
    // For national teams, use FIFA country codes
    return `https://a.espncdn.com/i/teamlogos/soccer/500/fifa/${team.abbreviation.toLowerCase()}.png`
  } else if (league.includes('ncaa') || league.includes('division i') || team.sport === 'College') {
    // For college teams, use ESPN CDN with verified team IDs
    // This provides comprehensive coverage with high-quality logos
    return `https://a.espncdn.com/i/teamlogos/ncaa/500/{TEAM_ID}.png`
  } else if (league.includes('formula 1') || team.sport === 'Racing') {
    // For F1 teams, try motorsports logos
    return `https://a.espncdn.com/i/teamlogos/racing/500/${team.abbreviation.toLowerCase()}.png`
  }
  
  return null
}

/**
 * Maps our league names to ESPN league codes
 */
function getEspnLeagueCode(team: Team): string {
  const league = (team.league || "").toLowerCase()
  
  // Map league names to ESPN codes
  if (league.includes('nba')) return 'nba'
  if (league.includes('mlb') || league.includes('american league') || league.includes('national league')) return 'mlb'
  if (league.includes('nfl')) return 'nfl'
  if (league.includes('nhl')) return 'nhl'
  if (league.includes('mls')) return 'mls'
  
  // Default fallback
  return 'nba'
}

/**
 * Gets multiple potential logo URLs for a team, in order of preference
 * This allows for robust fallback handling in components
 * 
 * For college teams: Prioritizes Wikimedia Commons SVGs over ESPN batching
 * For pro teams: Uses ESPN CDN with fallbacks
 */
export function getTeamLogoUrls(team: Team): string[] {
  const urls: string[] = []
  
  // 1. If the team already has a logoUrl defined, use it first
  if (team.logoUrl && team.logoUrl.trim() !== "") {
    urls.push(team.logoUrl)
  }

  // 2. For college teams, prioritize Wikimedia Commons (high quality SVGs)
  if (team.sport === 'College' && team.abbreviation) {
    // Check for enhanced logo from API first
    const enhancedLogo = getEnhancedCollegeLogo(team);
    if (enhancedLogo) {
      urls.push(enhancedLogo);
    }
    
    // Add standard Wikimedia Commons URLs (sync version for performance)
    const wikimediaLogos = getWikimediaCollegeLogoUrlsSync(team.abbreviation, team.name)
    urls.push(...wikimediaLogos)
    
    // DISABLED: Background enhancement causes too many requests
    // Only enhance on-demand when user explicitly requests it
    // enhanceCollegeTeamLogo(team).catch(() => {});
  }
  
  // 3. Try constructed ESPN logo (often 404s, but worth trying)
  const espnLogoUrl = getEspnLogoUrl(team)
  if (espnLogoUrl) {
    urls.push(espnLogoUrl)
  }

  // 4. Local logo by abbreviation (try both PNG and SVG)
  if (team.abbreviation) {
    const league = normalizeLeagueName(team.league || "unknown")
    const abbreviation = team.abbreviation.toLowerCase()
    urls.push(`/logos/${league}/${abbreviation}.png`)
    urls.push(`/logos/${league}/${abbreviation}.svg`)
  }
  
  // 5. Local logo by normalized team name (try both PNG and SVG)
  const league = normalizeLeagueName(team.league || "unknown")
  const normalizedTeamName = team.name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
  urls.push(`/logos/${league}/${normalizedTeamName}.png`)
  urls.push(`/logos/${league}/${normalizedTeamName}.svg`)
  
  // 6. Generic placeholder
  urls.push("/placeholder-logo.png")
  
  return urls
}

/**
 * Gets the primary logo URL (first preference) for a team
 */
export function getTeamLogoUrl(team: Team): string {
  const urls = getTeamLogoUrls(team)
  return urls[0] || "/placeholder-logo.png"
}

/**
 * Normalizes league name for filesystem compatibility
 */
function normalizeLeagueName(league: string): string {
  const normalized = league.toLowerCase();
  
  // Handle specific league mappings
  if (normalized.includes('ncaa') || normalized.includes('division i') || normalized.includes('college')) {
    return 'college';
  }
  if (normalized.includes('american league') || normalized.includes('national league')) {
    return 'mlb';
  }
  
  // Default normalization
  return league
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/**
 * Gets the appropriate team colors based on league and team information
 * Returns primary and secondary colors
 */
export function getTeamColors(team: Team): { primary: string; secondary: string } {
  // Check for colors in both camelCase and snake_case formats
  const primaryColor = team.primaryColor || (team as any).primary_color
  const secondaryColor = team.secondaryColor || (team as any).secondary_color
  
  // If the team has colors defined, use them
  if (primaryColor && secondaryColor) {
    return {
      primary: primaryColor,
      secondary: secondaryColor,
    }
  }
  
  // Default colors by league
  const defaultColors: Record<string, { primary: string; secondary: string }> = {
    mlb: { primary: "#002D72", secondary: "#E4002C" }, // MLB blue and red
    nba: { primary: "#17408B", secondary: "#C9082A" }, // NBA blue and red
    nfl: { primary: "#013369", secondary: "#D50A0A" }, // NFL blue and red
    nhl: { primary: "#000000", secondary: "#FFFFFF" }, // NHL black and white
    mls: { primary: "#1D4289", secondary: "#52B2E8" }, // MLS blue
  }
  
  const league = (team.league || "unknown").toLowerCase()
  return defaultColors[league] || { primary: "#333333", secondary: "#FFFFFF" }
}

/**
 * Returns the team name with appropriate formatting
 */
export function formatTeamName(team: Team): string {
  return `${team.city} ${team.name}`
}

/**
 * Checks if a team's logo file exists
 * This is a client-side function that doesn't actually check the filesystem
 * Instead, it's used to handle the onError callback for Image components
 */
export function hasTeamLogo(team: Team): boolean {
  // If the team has a defined logoUrl, assume it exists
  if (team.logoUrl && team.logoUrl.trim() !== "") {
    return true
  }
  
  // Otherwise we'll handle this with onError in the Image component
  return false
}

/**
 * Determines whether to show a fallback for team logos
 * Returns false if the team has a valid logo URL, true if we should use a fallback
 */
export function shouldUseFallbackLogo(logoUrl: string | null | undefined): boolean {
  return !logoUrl || logoUrl.trim() === "" || logoUrl.includes('/api/placeholder-image')
}

/**
 * Checks if a logo URL is from ESPN
 */
export function isEspnLogoUrl(logoUrl: string): boolean {
  return logoUrl.includes('espncdn.com')
}
